import React, { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, Plus, RefreshCw, Minus, AlertCircle, ChevronDown, ChevronRight, Package, Palette, Wand2 } from "lucide-react";

/**
 * Modale di anteprima diff dopo il parsing PDF.
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - parsedData: dict — il JSON `data` restituito dal job (necessario per l'apply)
 *   - jobId: string
 *   - onApplied: (result) => void
 */
export default function DiffPreviewModal({ open, onClose, parsedData, jobId, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectedTessuti, setSelectedTessuti] = useState(new Set());
  const [selectedGambe, setSelectedGambe] = useState(new Set());
  const [selectedMetalli, setSelectedMetalli] = useState(new Set());
  const [expandedTab, setExpandedTab] = useState("products");
  const [expandedProduct, setExpandedProduct] = useState(null);

  // Carica preview quando si apre
  React.useEffect(() => {
    if (!open || !jobId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.post("/ai/preview-parsed", { job_id: jobId });
        setPreview(r.data);
        // Preselezione: tutti i NEW + tutti gli UPDATED (comportamento sicuro di default)
        const nextP = new Set([
          ...r.data.products.new.map((p) => p.sku),
          ...r.data.products.updated.map((p) => p.sku),
        ]);
        setSelectedProducts(nextP);
        setSelectedTessuti(new Set([
          ...r.data.libraries.tessuti.new.map((x) => x.codice),
          ...r.data.libraries.tessuti.updated.map((x) => x.codice),
        ]));
        setSelectedGambe(new Set([
          ...r.data.libraries.gambe.new.map((x) => x.codice),
          ...r.data.libraries.gambe.updated.map((x) => x.codice),
        ]));
        setSelectedMetalli(new Set([
          ...r.data.libraries.metalli.new.map((x) => x.codice),
          ...r.data.libraries.metalli.updated.map((x) => x.codice),
        ]));
      } catch (e) {
        toast.error("Errore preview: " + (e.response?.data?.detail || e.message));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, jobId, onClose]);

  const toggle = (setState) => (id) => {
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (items, setState, keyFn = (x) => x.codice || x.sku) => {
    setState((prev) => {
      const allIds = items.map(keyFn);
      const allSelected = allIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...allIds]);
    });
  };

  const applySelected = async () => {
    if (!parsedData) {
      toast.error("Dati parsati mancanti");
      return;
    }
    const totSel = selectedProducts.size + selectedTessuti.size + selectedGambe.size + selectedMetalli.size;
    if (totSel === 0) {
      toast.warning("Seleziona almeno un elemento da applicare");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/ai/apply-parsed", {
        data: parsedData,
        selection: {
          product_skus: Array.from(selectedProducts),
          tessuti_codici: Array.from(selectedTessuti),
          gambe_codici: Array.from(selectedGambe),
          metalli_codici: Array.from(selectedMetalli),
        },
      });
      toast.success(
        `Applicati: ${r.data.applied.tessuti} tessuti · ${r.data.applied.gambe} gambe · ${r.data.applied.metalli} metalli · ${r.data.applied.products_created} nuovi · ${r.data.applied.products_updated} aggiornati`
      );
      onApplied?.(r.data);
      onClose();
    } catch (e) {
      toast.error("Errore apply: " + (e.response?.data?.detail || e.message));
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  const s = preview?.summary;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="diff-preview-modal">
      <div className="bg-white rounded-sm w-full max-w-6xl max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-brand font-semibold inline-flex items-center gap-1.5">
              <Wand2 className="w-3 h-3" /> Anteprima modifiche
            </div>
            <h2 className="font-heading font-bold text-2xl">Rivedi e conferma cosa applicare</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Seleziona con la spunta gli elementi da scrivere nel database. Nessuna modifica viene applicata finché non premi &quot;Applica selezionati&quot;.
            </p>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-sm hover:bg-slate-100 inline-flex items-center justify-center" data-testid="diff-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <div className="ml-3 text-sm text-muted-foreground">Calcolo differenze in corso…</div>
          </div>
        ) : preview ? (
          <>
            {/* Summary counters */}
            <div className="grid grid-cols-4 gap-3 p-5 bg-slate-50 border-b border-border" data-testid="diff-summary">
              <SummaryCard
                icon={<Package className="w-4 h-4" />}
                label="Prodotti"
                stats={s.products}
                active={expandedTab === "products"}
                onClick={() => setExpandedTab("products")}
                testId="tab-products"
              />
              <SummaryCard
                icon={<Palette className="w-4 h-4" />}
                label="Tessuti"
                stats={s.tessuti}
                active={expandedTab === "tessuti"}
                onClick={() => setExpandedTab("tessuti")}
                testId="tab-tessuti"
              />
              <SummaryCard
                label="Gambe"
                stats={s.gambe}
                active={expandedTab === "gambe"}
                onClick={() => setExpandedTab("gambe")}
                testId="tab-gambe"
              />
              <SummaryCard
                label="Metalli"
                stats={s.metalli}
                active={expandedTab === "metalli"}
                onClick={() => setExpandedTab("metalli")}
                testId="tab-metalli"
              />
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {expandedTab === "products" && (
                <ProductsPanel
                  products={preview.products}
                  selected={selectedProducts}
                  toggle={toggle(setSelectedProducts)}
                  toggleAll={(items) => toggleAll(items, setSelectedProducts, (x) => x.sku)}
                  expandedProduct={expandedProduct}
                  setExpandedProduct={setExpandedProduct}
                />
              )}
              {expandedTab === "tessuti" && (
                <LibraryPanel
                  axisLabel="Tessuti"
                  lib={preview.libraries.tessuti}
                  selected={selectedTessuti}
                  toggle={toggle(setSelectedTessuti)}
                  toggleAll={(items) => toggleAll(items, setSelectedTessuti)}
                />
              )}
              {expandedTab === "gambe" && (
                <LibraryPanel
                  axisLabel="Gambe"
                  lib={preview.libraries.gambe}
                  selected={selectedGambe}
                  toggle={toggle(setSelectedGambe)}
                  toggleAll={(items) => toggleAll(items, setSelectedGambe)}
                />
              )}
              {expandedTab === "metalli" && (
                <LibraryPanel
                  axisLabel="Metalli"
                  lib={preview.libraries.metalli}
                  selected={selectedMetalli}
                  toggle={toggle(setSelectedMetalli)}
                  toggleAll={(items) => toggleAll(items, setSelectedMetalli)}
                />
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-border p-4 flex items-center justify-between bg-slate-50">
              <div className="text-xs text-muted-foreground">
                Selezionati:&nbsp;
                <strong className="text-foreground" data-testid="diff-total-selected">
                  {selectedProducts.size} prodotti · {selectedTessuti.size} tessuti · {selectedGambe.size} gambe · {selectedMetalli.size} metalli
                </strong>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-white" data-testid="diff-cancel">
                  Annulla
                </button>
                <button
                  onClick={applySelected}
                  disabled={applying}
                  data-testid="diff-apply"
                  className="h-10 px-5 rounded-sm bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {applying ? "Applico…" : "Applica selezionati"}
                </button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ─── Summary card ────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, stats, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-left p-3 rounded-sm border transition-all ${active ? "border-brand bg-white shadow-sm" : "border-transparent hover:border-slate-200 bg-white/60"}`}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
      {icon} {label}
    </div>
    <div className="flex items-baseline gap-3 flex-wrap">
      {stats.new > 0 && (
        <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold">
          <Plus className="w-3 h-3" /> {stats.new}
        </span>
      )}
      {stats.updated > 0 && (
        <span className="inline-flex items-center gap-1 text-amber-700 text-sm font-semibold">
          <RefreshCw className="w-3 h-3" /> {stats.updated}
        </span>
      )}
      {stats.unchanged > 0 && (
        <span className="inline-flex items-center gap-1 text-slate-500 text-sm">
          <Minus className="w-3 h-3" /> {stats.unchanged}
        </span>
      )}
      {stats.missing_from_pdf > 0 && (
        <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium" title="Presenti in DB ma NON nel PDF">
          <AlertCircle className="w-3 h-3" /> {stats.missing_from_pdf} mancanti
        </span>
      )}
      {stats.new === 0 && stats.updated === 0 && stats.unchanged === 0 && (
        <span className="text-xs text-muted-foreground italic">nessun elemento</span>
      )}
    </div>
  </button>
);

// ─── Products panel ──────────────────────────────────────────────────────────
const ProductsPanel = ({ products, selected, toggle, toggleAll, expandedProduct, setExpandedProduct }) => {
  const [showTab, setShowTab] = useState("all");
  const filtered = useMemo(() => {
    if (showTab === "new") return products.new.map((p) => ({ ...p, _type: "new" }));
    if (showTab === "updated") return products.updated.map((p) => ({ ...p, _type: "updated" }));
    if (showTab === "unchanged") return products.unchanged.map((p) => ({ ...p, _type: "unchanged" }));
    return [
      ...products.new.map((p) => ({ ...p, _type: "new" })),
      ...products.updated.map((p) => ({ ...p, _type: "updated" })),
      ...products.unchanged.map((p) => ({ ...p, _type: "unchanged" })),
    ];
  }, [products, showTab]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <TabButton active={showTab === "all"} onClick={() => setShowTab("all")}>Tutti ({products.new.length + products.updated.length + products.unchanged.length})</TabButton>
        <TabButton active={showTab === "new"} onClick={() => setShowTab("new")} color="emerald">
          <Plus className="w-3 h-3" /> Nuovi ({products.new.length})
        </TabButton>
        <TabButton active={showTab === "updated"} onClick={() => setShowTab("updated")} color="amber">
          <RefreshCw className="w-3 h-3" /> Modificati ({products.updated.length})
        </TabButton>
        <TabButton active={showTab === "unchanged"} onClick={() => setShowTab("unchanged")}>
          <Minus className="w-3 h-3" /> Invariati ({products.unchanged.length})
        </TabButton>
        <div className="ml-auto flex items-center gap-2">
          {products.missing_from_pdf?.length > 0 && (
            <span className="text-[10px] text-red-700 bg-red-50 px-2 py-1 rounded-sm border border-red-200" title={products.missing_from_pdf.slice(0, 10).join(", ")}>
              {products.missing_from_pdf.length} nel DB non nel PDF (non toccati)
            </span>
          )}
          <button
            onClick={() => toggleAll(filtered.filter((p) => p._type !== "unchanged"))}
            className="text-xs text-brand hover:underline"
            data-testid="diff-prod-selectall"
          >
            Toggle tutti (escl. invariati)
          </button>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground italic">Nessun prodotto in questa categoria.</div>
      )}

      <div className="space-y-1.5">
        {filtered.map((p) => (
          <ProductRow
            key={p.sku}
            product={p}
            checked={selected.has(p.sku)}
            onToggle={() => toggle(p.sku)}
            expanded={expandedProduct === p.sku}
            onExpand={() => setExpandedProduct(expandedProduct === p.sku ? null : p.sku)}
          />
        ))}
      </div>
    </div>
  );
};

const ProductRow = ({ product, checked, onToggle, expanded, onExpand }) => {
  const typeStyle = {
    new: "border-l-emerald-500 bg-emerald-50/40",
    updated: "border-l-amber-500 bg-amber-50/40",
    unchanged: "border-l-slate-300 bg-slate-50/40",
  }[product._type];
  const hasChanges = product._type === "updated" && product.changes?.length > 0;

  return (
    <div className={`border border-slate-200 border-l-4 ${typeStyle} rounded-sm`}>
      <div className="flex items-center gap-3 p-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          disabled={product._type === "unchanged"}
          data-testid={`diff-prod-chk-${product.sku}`}
          className="w-4 h-4 accent-brand disabled:opacity-30"
        />
        {product.cover_url && (
          <img src={product.cover_url} alt="" className="w-10 h-10 rounded-sm object-cover border border-border" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <TypePill type={product._type} />
            <span className="font-mono text-xs font-semibold">{product.sku}</span>
            <span className="text-sm truncate">{product.nome}</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {product._type === "new" && (
              <>
                Cat. <strong>{product.categoria}</strong> · € {product.prezzo_base?.toFixed(2)} · {product.immagini_count} img · {product.variant_axes_count} assi
              </>
            )}
            {product._type === "updated" && (
              <>{product.changes.length} modifiche</>
            )}
            {product._type === "unchanged" && <>nessuna modifica rilevata</>}
          </div>
        </div>
        {hasChanges && (
          <button onClick={onExpand} className="h-7 w-7 rounded-sm hover:bg-white inline-flex items-center justify-center" data-testid={`diff-prod-expand-${product.sku}`}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        )}
      </div>

      {expanded && hasChanges && (
        <div className="border-t border-slate-200 bg-white p-3">
          <div className="space-y-1.5">
            {product.changes.map((c, i) => (
              <ChangeRow key={i} change={c} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ChangeRow = ({ change }) => {
  const label = change.label || change.field;
  if (change.field === "immagini") {
    return (
      <div className="text-xs">
        <div className="font-semibold mb-1">{label}: +{change.added_count} nuove ({change.old_count} già presenti)</div>
        <div className="flex gap-1.5 flex-wrap">
          {(change.new_urls_preview || []).map((url, i) => (
            <img key={i} src={url} alt="" className="w-14 h-14 rounded-sm object-cover border border-emerald-300" />
          ))}
        </div>
      </div>
    );
  }
  if (change.field === "variant_axes") {
    return <div className="text-xs"><strong>{label}:</strong> +{change.added_options} opzioni aggiunte</div>;
  }
  return (
    <div className="text-xs grid grid-cols-[120px_1fr_1fr] gap-2 items-start">
      <div className="text-muted-foreground uppercase tracking-wider text-[10px] pt-0.5">{label}</div>
      <div className="bg-red-50 border border-red-200 rounded-sm px-2 py-1 line-through decoration-red-500 text-red-900 break-words">
        {formatVal(change.old)}
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1 text-emerald-900 break-words">
        {formatVal(change.new)}
      </div>
    </div>
  );
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === "") return <span className="italic text-slate-400">vuoto</span>;
  if (typeof v === "number") return v.toString();
  const str = String(v);
  return str.length > 200 ? str.slice(0, 200) + "…" : str;
};

const TypePill = ({ type }) => {
  const map = {
    new: { txt: "NUOVO", cls: "bg-emerald-100 text-emerald-800" },
    updated: { txt: "MODIFICATO", cls: "bg-amber-100 text-amber-900" },
    unchanged: { txt: "INVARIATO", cls: "bg-slate-100 text-slate-600" },
  };
  const m = map[type] || map.unchanged;
  return <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm ${m.cls}`}>{m.txt}</span>;
};

const TabButton = ({ active, onClick, children, color }) => {
  const colorMap = {
    emerald: active ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100",
    amber: active ? "bg-amber-600 text-white border-amber-600" : "bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100",
    default: active ? "bg-foreground text-white border-foreground" : "bg-white text-foreground border-border hover:bg-slate-50",
  };
  return (
    <button
      onClick={onClick}
      className={`h-8 px-3 rounded-sm text-xs font-medium border inline-flex items-center gap-1 ${colorMap[color || "default"]}`}
    >
      {children}
    </button>
  );
};

// ─── Library panel (tessuti/gambe/metalli) ───────────────────────────────────
const LibraryPanel = ({ axisLabel, lib, selected, toggle, toggleAll }) => {
  const items = [
    ...lib.new.map((x) => ({ ...x, _type: "new" })),
    ...lib.updated.map((x) => ({ ...x, _type: "updated" })),
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm">
          <strong>{axisLabel}</strong> · {lib.new.length} nuovi · {lib.updated.length} modificati · {lib.unchanged_count} invariati (già ok)
        </div>
        {items.length > 0 && (
          <button onClick={() => toggleAll(items)} className="text-xs text-brand hover:underline" data-testid={`diff-lib-${axisLabel.toLowerCase()}-selectall`}>
            Toggle tutti
          </button>
        )}
      </div>
      {items.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground italic">Nessun elemento nuovo o modificato per {axisLabel.toLowerCase()}.</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {items.map((it) => (
          <label
            key={`${it._type}-${it.codice}`}
            className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer hover:bg-slate-50 ${it._type === "new" ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}`}
          >
            <input
              type="checkbox"
              checked={selected.has(it.codice)}
              onChange={() => toggle(it.codice)}
              data-testid={`diff-lib-chk-${it.codice}`}
              className="w-4 h-4 accent-brand"
            />
            <TypePill type={it._type} />
            <span className="font-mono text-xs font-semibold">{it.codice}</span>
            <span className="text-xs flex-1 truncate">{it.nome}</span>
            {it._type === "updated" && it.changes && (
              <span className="text-[10px] text-amber-800">{it.changes.length} campi</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
};
